import type { Model3D } from './types';

/**
 * Artwork lookup.
 *
 * Content files refer to art by id ("kucing/utuh"), never by path, so that
 * moving or renaming files never touches a lesson. This resolves an id to the
 * SVG source text.
 *
 * Source text rather than an <img> or a background-image, because both the
 * hotspot screen and the colouring screen need to reach inside the drawing and
 * address individual regions. An <img> is opaque; only inlined SVG can be
 * queried and repainted.
 *
 * `import.meta.glob` builds the map at build time, so adding a drawing means
 * dropping a file in - there is no registry to update. Each SVG becomes its own
 * chunk, which keeps a lesson's download to the art it actually uses.
 */

const files = import.meta.glob(['./**/*.svg', '!./**/*.pose-*.svg'], {
  query: '?raw',
  import: 'default',
}) as Record<string, () => Promise<string>>;

async function loadFile(id: string): Promise<string | null> {
  const loader = files[`./${id}.svg`];
  if (!loader) return null;
  try {
    return await loader();
  } catch {
    return null;
  }
}

export async function loadArt(id: string): Promise<string | null> {
  return (await loadFile(id)) ?? (await derivePart(id));
}

/**
 * Parts: `<subjek>/wajah` is the face of `<subjek>/utuh`, not a drawing of its
 * own.
 *
 * A face card and a whole animal drawn separately are two faces to keep in
 * agreement - redraw the ears on one and the other is wrong. So when no file
 * exists for a part, it is cut out of the whole: the whole drawing declares the
 * crop on its root (`data-crop-wajah="x y w h"`) and marks the groups that
 * belong in it (`data-wajah`). Everything else is dropped, and the viewBox
 * becomes the crop. The drawing says where its face is; nothing here knows.
 *
 * A real file at the part's path still wins, so a subject that genuinely needs
 * a different face card can have one.
 */
const PARTS = ['wajah'] as const;

/** The whole a part id belongs to: `anjing/wajah` -> `anjing/utuh`. */
export function wholeOf(id: string): string | null {
  const slash = id.lastIndexOf('/');
  if (slash < 0) return null;
  const part = id.slice(slash + 1);
  return (PARTS as readonly string[]).includes(part) ? `${id.slice(0, slash)}/utuh` : null;
}

/** True when a part id has no file and is served by cropping its whole. */
export function isDerived(id: string): boolean {
  return wholeOf(id) !== null && !files[`./${id}.svg`];
}

async function derivePart(id: string): Promise<string | null> {
  const whole = wholeOf(id);
  if (!whole) return null;
  const source = await loadFile(whole);
  if (!source) return null;

  const part = id.slice(id.lastIndexOf('/') + 1);
  const doc = new DOMParser().parseFromString(source, 'image/svg+xml');
  const svg = doc.documentElement;
  const crop = svg.getAttribute(`data-crop-${part}`);
  // A whole that has not declared this part cannot be cropped honestly -
  // guessing a box would put half an ear on a lesson card. Report it missing.
  if (!crop || doc.querySelector('parsererror')) return null;

  svg.querySelectorAll(`[data-depth]:not([data-${part}])`).forEach((group) => group.remove());
  svg.setAttribute('viewBox', crop);
  svg.removeAttribute(`data-crop-${part}`);
  svg.setAttribute('aria-label', `${svg.getAttribute('aria-label') ?? 'Gambar'} (${part})`);

  return new XMLSerializer().serializeToString(svg);
}

/** Every art id that exists, for tooling and for honest "missing asset" lists. */
export function artIds(): string[] {
  return Object.keys(files).map((path) => path.replace(/^\.\//, '').replace(/\.svg$/, ''));
}

/**
 * Poses: `<id>.pose-<nama>.svg` beside `<id>.svg`.
 *
 * The same cat lying down is not a different asset. It has the same regions,
 * the same coat and the same contract, and no lesson will ever ask for it by an
 * id of its own - so it is excluded from `files` above and reached through here
 * instead. Without that exclusion every pose would appear on `#/aset` as a
 * drawing nobody references, which is exactly the signal that screen exists to
 * give about real gaps.
 *
 * Same idea as `*.model.ts`: a suffixed sibling is a counterpart of an id, not
 * a new one.
 */
const poseFiles = import.meta.glob('./**/*.pose-*.svg', {
  query: '?raw',
  import: 'default',
}) as Record<string, () => Promise<string>>;

export interface ArtPose {
  /** The bit after `pose-`: `tidur`, `meringkuk`, `roti`. */
  name: string;
  svg: string;
}

/**
 * Pose names for an id, without loading any of them. Cheap enough for a list
 * screen that would otherwise have to parse every drawing twice.
 */
export function poseNames(id: string): string[] {
  const prefix = `./${id}.pose-`;
  return Object.keys(poseFiles)
    .filter((path) => path.startsWith(prefix))
    .map((path) => path.slice(prefix.length).replace(/\.svg$/, ''))
    .sort();
}

/** Every pose that exists for an id, alphabetical. Empty when there are none. */
export async function loadPoses(id: string): Promise<ArtPose[]> {
  const prefix = `./${id}.pose-`;

  const found = Object.keys(poseFiles)
    .filter((path) => path.startsWith(prefix))
    .sort();

  const poses = await Promise.all(
    found.map(async (path): Promise<ArtPose | null> => {
      try {
        return {
          name: path.slice(prefix.length).replace(/\.svg$/, ''),
          svg: await poseFiles[path]!(),
        };
      } catch {
        return null;
      }
    }),
  );

  return poses.filter((pose): pose is ArtPose => pose !== null);
}

/**
 * The 3D counterpart of `loadArt`.
 *
 * A model is a TypeScript module rather than a file format, because primitives
 * assembled in code is what this repo has instead of an art pipeline. Same id
 * space as the SVGs on purpose: `kucing/utuh` resolves to a drawing here and a
 * model there, so one content file addresses both.
 */
const models = import.meta.glob('./**/*.model.ts', { import: 'model' }) as Record<
  string,
  () => Promise<Model3D>
>;

export async function loadModel(id: string): Promise<Model3D | null> {
  const loader = models[`./${id}.model.ts`];
  if (!loader) return null;
  try {
    return await loader();
  } catch {
    return null;
  }
}
